/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * useDivision Hook
 *
 * Manages single division data fetching and updates
 */

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import { QUERY_GC_TIME, QUERY_STALE_TIME } from "@/lib/constants/query-config";
import type {
  Division,
  DivisionUpdate,
} from "@/types/budget/divisional-budgets";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";

// ============================================================================
// TYPES
// ============================================================================

export interface DivisionState {
  division: Division | null;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// HOOK
// ============================================================================

export function useDivision(divisionId: string | null) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled, isOrganizationReady, isAuthReady } =
    useOrganizationQueryGuard({
      enabled: !!divisionId,
      debugKey: "useDivision",
    });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  // ============================================================================
  // FETCH SINGLE DIVISION
  // ============================================================================

  const divisionQuery = useQuery<{ division: Division }>({
    queryKey: ["division", organizationId, divisionId],
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: QUERY_STALE_TIME.STABLE,
    gcTime: QUERY_GC_TIME.STABLE,
    meta: { entity: "division" },
    queryFn: async () => {
      if (!organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }

      const res = await api.get<Division>(`/api/divisions/${divisionId}`);

      if (isApiFailure(res)) {
        throw new Error(res.message);
      }

      const division = res.data as Division | null;
      if (!division?.division_id) {
        throw new Error("Division not found");
      }

      return { division };
    },
  });

  const divisionState = useMemo<DivisionState>(
    () => ({
      division: divisionQuery.data?.division ?? null,
      // Show loading if we have a divisionId and:
      // 1. Auth is not ready yet (waiting for auth)
      // 2. Query is loading or fetching
      // 3. Query is enabled but hasn't returned data yet (and no error)
      loading:
        !!divisionId &&
        (!isAuthReady ||
          divisionQuery.isLoading ||
          divisionQuery.isFetching ||
          (!divisionQuery.data && !divisionQuery.error)),
      error: divisionQuery.error?.message ?? null,
    }),
    [
      divisionQuery.data,
      divisionQuery.isLoading,
      divisionQuery.isFetching,
      divisionQuery.error,
      divisionId,
      isAuthReady,
    ],
  );

  // ============================================================================
  // UPDATE DIVISION
  // ============================================================================

  const updateDivisionMutation = useMutation({
    mutationFn: async (updates: DivisionUpdate) => {
      if (!divisionId || !organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }

      const res = await api.patch<Division>(
        `/api/divisions/${divisionId}`,
        updates,
      );

      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to update division", res);
        throw new Error(res.message);
      }

      return res.data as Division;
    },
    onMutate: async (updates) => {
      const detailKey = ["division", organizationId, divisionId];
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Division>(detailKey);
      queryClient.setQueryData<Division | undefined>(detailKey, (old) =>
        old ? { ...old, ...updates } : old,
      );
      return { previous, detailKey };
    },
    onError: (_err, _updates, context) => {
      if (context?.previous)
        queryClient.setQueryData(context.detailKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["division", organizationId, divisionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["divisions", organizationId],
      });
    },
  });

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    ...divisionState,
    isLoading: divisionState.loading,
    hasError: !!divisionState.error,

    // Actions
    updateDivision: updateDivisionMutation.mutateAsync,
    refetch: divisionQuery.refetch,

    // Mutations (for direct access if needed)
    updateDivisionMutation,
  };
}
